import React, { useState, useEffect, useCallback } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import LinearProgress from '@mui/material/LinearProgress';
import CircularProgress from '@mui/material/CircularProgress';

import StatusChip from './StatusChip';
import DashboardErrorBanner from './DashboardErrorBanner';
import {
  getDespatchDashboard,
  DespatchDashboardData,
  DeliveryCard as DeliveryCardData,
} from '../../services/wmsDashboardService';

export default function DespatchDashboard() {
  const [data, setData] = useState<DespatchDashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [secondsAgo, setSecondsAgo] = useState<number | null>(null);

  const fetchData = useCallback(async () => {
    try {
      const result = await getDespatchDashboard();
      setData(result);
      setLastUpdated(new Date());
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Unable to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial fetch
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Polling: 15s with visibility pause
  useEffect(() => {
    let intervalId: NodeJS.Timeout;

    const start = () => {
      intervalId = setInterval(fetchData, 15000);
    };
    const stop = () => {
      clearInterval(intervalId);
    };

    const onVisibility = () => {
      if (document.hidden) {
        stop();
      } else {
        fetchData();
        start();
      }
    };

    start();
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [fetchData]);

  // "Updated Xs ago" counter
  useEffect(() => {
    if (!lastUpdated) return;

    const tick = () => {
      setSecondsAgo(Math.floor((Date.now() - lastUpdated.getTime()) / 1000));
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [lastUpdated]);

  // Group deliveries by status
  const activeDeliveries = data?.deliveries.filter((d) => d.status === 'active') || [];
  const readyDeliveries = data?.deliveries.filter((d) => d.status === 'ready') || [];
  const completedDeliveries = data?.deliveries.filter((d) => d.status === 'completed') || [];

  const renderDeliveryCard = (delivery: DeliveryCardData) => {
    const progress =
      delivery.expected_pallets > 0
        ? (delivery.loaded_pallets / delivery.expected_pallets) * 100
        : 0;

    const timestamp = delivery.completed_at || delivery.started_at;

    return (
      <Card
        key={`${delivery.customer_code}-${delivery.id}`}
        sx={{
          borderRadius: '12px',
          mb: 2,
          borderLeft:
            delivery.status === 'active'
              ? '4px solid #CB6015'
              : delivery.status === 'completed'
              ? '4px solid #2E7D32'
              : 'none',
          opacity: delivery.status === 'completed' ? 0.7 : 1,
        }}
      >
        <CardContent sx={{ p: 2 }}>
          {/* Top row: Customer + License plate */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              {delivery.customer_name || delivery.customer_code}
            </Typography>
            {delivery.license_plate && (
              <Chip variant="outlined" label={delivery.license_plate} />
            )}
          </Box>

          {/* Progress row */}
          <Box sx={{ mt: 1.5 }}>
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{
                height: 8,
                borderRadius: 4,
                backgroundColor: '#E0E0E0',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: '#CB6015',
                },
              }}
            />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {delivery.loaded_pallets} of {delivery.expected_pallets} loaded
            </Typography>
          </Box>

          {/* Bottom row: Status chip + timestamp */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5 }}>
            <StatusChip status={delivery.status} />
            {timestamp && (
              <Typography variant="caption" color="text.secondary">
                {new Date(timestamp).toLocaleTimeString()}
              </Typography>
            )}
          </Box>
        </CardContent>
      </Card>
    );
  };

  return (
    <Box sx={{ width: '100%', flexGrow: 1 }}>
      {/* Header summary bar — layout provides the page title */}
      {data && (
        <Paper
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-around',
            p: 2,
            borderRadius: '12px',
            mb: 3,
          }}
        >
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              {data.summary.picks_outstanding}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Picks Outstanding
            </Typography>
          </Box>
          <Divider orientation="vertical" flexItem />
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              {data.summary.pallets_staged}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Pallets Staged
            </Typography>
          </Box>
          <Divider orientation="vertical" flexItem />
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              {data.summary.pallets_loaded}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Pallets Loaded
            </Typography>
          </Box>
        </Paper>
      )}

      {/* Updated timestamp */}
      {secondsAgo !== null && (
        <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
          Updated {secondsAgo}s ago
        </Typography>
      )}

      {/* Error state */}
      {error && <DashboardErrorBanner message={error} onRetry={fetchData} />}

      {/* Loading state */}
      {loading && !data && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Delivery sections */}
      {data && data.deliveries.length === 0 ? (
        <Paper
          sx={{
            p: 4,
            textAlign: 'center',
            borderRadius: '12px',
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
            No deliveries scheduled
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Deliveries will appear here once pick tasks are generated from the despatch schedule.
          </Typography>
        </Paper>
      ) : (
        data && (
          <>
            {/* Active Loading */}
            {activeDeliveries.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, mt: 3 }}>
                  <Typography variant="h5" sx={{ fontWeight: 600 }}>
                    Active Loading
                  </Typography>
                  <Chip label={activeDeliveries.length} size="small" sx={{ ml: 1 }} />
                </Box>
                {activeDeliveries.map(renderDeliveryCard)}
              </Box>
            )}

            {/* Staged and Ready */}
            {readyDeliveries.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, mt: 3 }}>
                  <Typography variant="h5" sx={{ fontWeight: 600 }}>
                    Staged and Ready
                  </Typography>
                  <Chip label={readyDeliveries.length} size="small" sx={{ ml: 1 }} />
                </Box>
                {readyDeliveries.map(renderDeliveryCard)}
              </Box>
            )}

            {/* Completed Today */}
            {completedDeliveries.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, mt: 3 }}>
                  <Typography variant="h5" sx={{ fontWeight: 600 }}>
                    Completed Today
                  </Typography>
                  <Chip label={completedDeliveries.length} size="small" sx={{ ml: 1 }} />
                </Box>
                {completedDeliveries.map(renderDeliveryCard)}
              </Box>
            )}
          </>
        )
      )}
    </Box>
  );
}
